/*
 * My Trades (draft 9, client hub rework 2026-10-30): the clients' own hub, built for phones first (most clients open it
 * from a text). Sign in with a phone and a texted code; then Home, Visits, Invoices, Get a quote and More for every brand
 * they use, like Jobber's Client Hub (appointment and invoice cards, past due first) plus the old Steward's My Trades
 * (Add a visit, requests, Wallet to add or replace a card, never remove it, Refer a friend) plus the sister brands they do not use yet, each with Get a quote.
 * Add-ons (2026-11-02): the price list's add-ons go on an upcoming visit until the day before it (Add extras).
 *
 * Every page looks like the brand: its color band with its logo at the top and its color at the bottom, no Stripe branding
 * (Pay opens our own pay page; Wallet uses Stripe's card fields on our own page). Phones get a tab bar at the bottom and
 * sheets that slide up; computers get Jobber's side menu. The page lives in the address bar hash (#visits), so the phone's
 * back button works.
 *
 * My account on a pay or quote page and the My Trades link in our emails (2026-11-01): /my?i=<pay link>, ?q=<quote link> or
 * ?c=<the client's hub link> opens the hub with no code. It can look only; a sheet that changes something first asks for a
 * code texted to the client's own phone, which lets them make changes for an hour.
 *
 * View as client (2026-10-30): owners and managers open the same hub from a client's page (More, View as client). It
 * shows that client's hub for the office's own company, read only: anything that would change something says so instead.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";
import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  HOURLY_OFTEN_WORDS,
  HUB_DEFAULT_OFTEN,
  REQUEST_PHOTOS_MAX,
  type HomeAnswers,
  type HourlyAnswers,
  type HubPriceAnswer,
  type HubQuoterAnswer,
  type PortalAnswer,
  type PortalBrand,
  type PortalOther,
} from "@steward/shared";
import { api, ApiError } from "../api/client";
import { useSignInLook } from "../api/hooks";
import { clock, moneyExact, money, phoneText, shortDate, weekdayName } from "../components/format";
import { Icon, type IconName } from "../components/Icon";
import { Banner, Button, Skeleton } from "../components/parts";

const KEY = "steward.my.token";
const BRAND_KEY = "steward.my.brand";
const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string | null) => {
    try {
      if (v) localStorage.setItem(k, v);
      else localStorage.removeItem(k);
    } catch {
      // Private windows: the hub still works until the page closes.
    }
  },
};
/** True when the office is viewing as the client: nothing may change. */
const ReadOnly = createContext(false);
/** Opened from an invoice or quote link: the phone's last 4 and how to swap in the full session once the code is in. */
const ByLink = createContext<{ phoneEnd: string; token: string; onFull: (token: string) => void } | null>(null);
/** Nothing that changes something may start: viewing as the client, or opened from a link with no code yet. */
const useLocked = () => useContext(ReadOnly) || useContext(ByLink) !== null;
const errText = (e: unknown) => (e instanceof ApiError ? e.message : "That did not work. Try again.");

type Visit = PortalBrand["next"][number];
type PastVisit = PortalBrand["past"][number];
type Invoice = PortalBrand["invoices"][number];

const longDay = (d: string) => `${weekdayName(d)}, ${shortDate(d)}`;
const arrival = (v: Visit | PastVisit) =>
  v.arriveFrom && v.arriveTo
    ? `Arriving between ${clock(v.arriveFrom)} and ${clock(v.arriveTo)}`
    : v.startTime
      ? `Starts at ${clock(v.startTime)}`
      : "Time to be set";
const crewWords = (crew: string[]) =>
  crew.length ? `${crew.join(" and ")} ${crew.length === 1 ? "is" : "are"} coming` : "Your team is set the day before";
const mapLink = (a: string) => `https://maps.google.com/?q=${encodeURIComponent(a)}`;
const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString("en-CA");
};

/** What each trade does, in a line, and quick picks for Get a quote. */
const TRADE: Record<string, { pitch: string; picks: string[]; visit: string }> = {
  cleaning: {
    pitch: "House cleaning, deep cleans and move outs.",
    picks: ["Deep clean", "One time clean", "Move out clean", "Regular cleaning", "Rental turnover"],
    visit: "cleaning",
  },
  lawn: {
    pitch: "Lawn care, yard cleanups and landscaping.",
    picks: ["Lawn mowing", "Yard cleanup", "Hedge trimming", "Leaf removal", "Landscaping"],
    visit: "visit",
  },
  washers: {
    pitch: "Pressure washing for your house, driveway and roof.",
    picks: ["House wash", "Driveway cleaning", "Roof wash", "Window cleaning", "Gutter cleaning"],
    visit: "visit",
  },
  agency: {
    pitch: "Ads, Google and reviews that bring in more jobs.",
    picks: ["Facebook ads", "Google ads", "A new website", "More Google reviews"],
    visit: "visit",
  },
};
const tradeOf = (t: string) => TRADE[t] ?? { pitch: "", picks: [], visit: "visit" };

type Page = "home" | "visits" | "invoices" | "services" | "more" | "quotes" | "requests" | "wallet" | "refer" | "contact";
const PAGES: Page[] = ["home", "visits", "invoices", "services", "more", "quotes", "requests", "wallet", "refer", "contact"];
const TABS: { k: Page; label: string; icon: IconName }[] = [
  { k: "home", label: "Home", icon: "home" },
  { k: "visits", label: "Visits", icon: "schedule" },
  { k: "invoices", label: "Invoices", icon: "invoices" },
  { k: "services", label: "Get a quote", icon: "plusCircle" },
  { k: "more", label: "More", icon: "menu" },
];
const SIDE: { k: Page; label: string; icon: IconName }[] = [
  { k: "home", label: "Home", icon: "home" },
  { k: "visits", label: "Visits", icon: "schedule" },
  { k: "invoices", label: "Invoices", icon: "invoices" },
  { k: "quotes", label: "Quotes", icon: "quotes" },
  { k: "requests", label: "Book or ask", icon: "requests" },
  { k: "services", label: "Get a quote", icon: "plusCircle" },
  { k: "wallet", label: "Wallet", icon: "money" },
  { k: "refer", label: "Refer a friend", icon: "tag" },
  { k: "contact", label: "Contact us", icon: "phone" },
];
/** The tab a page sits under on a phone. */
const TAB_OF: Partial<Record<Page, Page>> = { quotes: "more", requests: "more", wallet: "more", refer: "more", contact: "more" };

/** The page in the address bar hash, so the phone's back button goes back a page. */
function usePage(): [Page, (p: Page) => void] {
  const read = (): Page => {
    const h = window.location.hash.slice(1) as Page;
    return PAGES.includes(h) ? h : "home";
  };
  const [page, setPage] = useState<Page>(read);
  useEffect(() => {
    const on = () => setPage(read());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return [
    page,
    (p) => {
      if (p !== page) window.location.hash = p;
      window.scrollTo({ top: 0 });
    },
  ];
}

/** The pay, quote or hub link in the address (?i=, ?q= or ?c=), taken out of the address once read. */
function linkInAddress(): { kind: "invoice" | "quote" | "client"; link: string } | null {
  const q = new URLSearchParams(window.location.search);
  const i = q.get("i");
  const qu = q.get("q");
  const c = q.get("c");
  if (!i && !qu && !c) return null;
  q.delete("i");
  q.delete("q");
  q.delete("c");
  const rest = q.toString();
  window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}${window.location.hash}`);
  return c ? { kind: "client", link: c } : i ? { kind: "invoice", link: i } : { kind: "quote", link: qu! };
}

export function MyTrades() {
  const [token, setToken] = useState<string | null>(() => store.get(KEY));
  // A link opens its own client's hub, even on a phone signed in before as someone else.
  const [link] = useState(() => linkInAddress());
  const [opening, setOpening] = useState(Boolean(link));
  useEffect(() => {
    if (!link) return;
    void api<{ token: string }>("POST", "public/my/link", link)
      .then((r) => {
        store.set(KEY, r.token);
        setToken(r.token);
      })
      .catch(() => null)
      .finally(() => setOpening(false));
  }, [link]);
  if (opening)
    return (
      <div className="public my">
        <header className="my-top">
          <div className="my-top-in" />
        </header>
        <main className="my-signin">
          <Skeleton rows={6} />
        </main>
      </div>
    );
  if (!token)
    return (
      <MySignIn
        onIn={(t) => {
          store.set(KEY, t);
          setToken(t);
        }}
      />
    );
  return (
    <MyHub
      token={token}
      onToken={(t) => {
        store.set(KEY, t);
        setToken(t);
      }}
      onOut={() => {
        void api("POST", "public/my/sign-out", {}, { "x-portal-token": token }).catch(() => null);
        store.set(KEY, null);
        setToken(null);
      }}
    />
  );
}

function MySignIn({ onIn }: { onIn: (token: string) => void }) {
  const look = useSignInLook().data?.brand ?? null;
  const band = look?.color ? ({ "--my-band": look.color, "--brand": look.color } as CSSProperties) : undefined;
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (!sent) {
        // Each brand's site links here with ?brand=<its slug>, so the code comes from that brand's line (answer 91). With no
        // slug, the brand at this web address sends it.
        const brand = new URLSearchParams(window.location.search).get("brand");
        const origin = window.location.origin;
        await api("POST", "public/my/code", brand ? { phone, brand, origin } : { phone, origin });
        setSent(true);
      } else onIn((await api<{ token: string }>("POST", "public/my/verify", { phone, code })).token);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="public my my-out" style={band}>
      <header className="my-top">
        <div className="my-top-in my-top-center">
          {look ? <Logo url={look.logoWhiteUrl ?? look.logoUrl} name={look.name} className="my-logo" /> : null}
        </div>
      </header>
      <main className="my-signin">
        <h1>{sent ? "Check your texts" : "Sign in to your account"}</h1>
        <p className="muted">
          {sent
            ? `We sent a 6 digit code to ${phone}. Type it below.`
            : "See your visits, invoices and receipts. Type your cell phone number and we will text you a code."}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void go();
          }}
        >
          {sent ? (
            <label className="field">
              <span className="label">Code</span>
              <div className="input">
                <input
                  className="my-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  autoFocus
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              </div>
            </label>
          ) : (
            <label className="field">
              <span className="label">Cell phone</span>
              <div className="input">
                <input
                  type="tel"
                  autoComplete="tel"
                  placeholder="(504) 555-0123"
                  value={phone}
                  autoFocus
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </label>
          )}
          {err ? <Banner type="error">{err}</Banner> : null}
          <Button
            size="large"
            fullWidth
            submit
            loading={busy}
            disabled={sent ? code.length !== 6 : phone.replace(/\D/g, "").length < 10}
          >
            {sent ? "Sign in" : "Text me a code"}
          </Button>
        </form>
        {sent ? (
          <Button
            type="tertiary"
            fullWidth
            onClick={() => {
              setSent(false);
              setCode("");
            }}
          >
            Use a different number
          </Button>
        ) : (
          <p className="muted my-hint">Got a text or email from us? Tap the link in it to go straight to your account.</p>
        )}
      </main>
      <div className="public-band my-out-foot" />
    </div>
  );
}

/** View as client: the office's own hub view of one client, read only. */
export function MyTradesAsClient({ clientId, onBack }: { clientId: string; onBack: () => void }) {
  return (
    <ReadOnly.Provider value={true}>
      <MyHub token="" onOut={onBack} asClient={clientId} />
    </ReadOnly.Provider>
  );
}

function MyHub({
  token,
  onOut,
  onToken,
  asClient,
}: {
  token: string;
  onOut: () => void;
  onToken?: (token: string) => void;
  asClient?: string;
}) {
  const hub = useQuery({
    queryKey: asClient ? ["my", "as-client", asClient] : ["my", token],
    queryFn: () =>
      asClient
        ? api<PortalAnswer>("GET", `clients/${asClient}/hub`)
        : api<PortalAnswer>("GET", "public/my", undefined, { "x-portal-token": token }),
    retry: false,
    // A code typed from a link swaps the token: keep showing the hub (and the open sheet) while it reloads.
    placeholderData: (prev) => prev,
  });
  const [page, go] = usePage();
  const [picked, setPicked] = useState<string | null>(() => store.get(BRAND_KEY));
  const [switching, setSwitching] = useState(false);
  const signedOut = !asClient && hub.error instanceof ApiError && hub.error.status === 401;
  useEffect(() => {
    if (signedOut) onOut();
  }, [signedOut, onOut]);
  if (hub.error)
    return (
      <div className="public my">
        <main className="my-signin">
          <Banner type="error">{errText(hub.error)}</Banner>
          <Button onClick={() => void hub.refetch()}>Try again</Button>
          {asClient ? (
            <Button type="tertiary" onClick={onOut}>
              Back to client
            </Button>
          ) : null}
        </main>
      </div>
    );
  if (!hub.data)
    return (
      <div className="public my">
        <header className="my-top">
          <div className="my-top-in" />
        </header>
        <main className="my-signin">
          <Skeleton rows={6} />
        </main>
      </div>
    );
  const { brands, others } = hub.data;
  // The hub opens on the brand whose web address this is; the client's other brands are a tap away.
  const b = brands.find((x) => x.companyId === picked) ?? brands.find((x) => x.url === window.location.origin) ?? brands[0];
  if (!b)
    return (
      <div className="public my">
        <main className="my-signin">
          <p>We could not find anything for this number yet.</p>
          <Button type="tertiary" onClick={onOut}>
            {asClient ? "Back to client" : "Log out"}
          </Button>
        </main>
      </div>
    );
  // Buttons and links use the darker shade so white text reads on them; the bands use the kit colors.
  const style = { "--brand": b.deep, "--my-band": b.color ?? b.deep, "--my-footc": b.foot } as CSSProperties;
  const tab = TAB_OF[page] ?? page;
  const props = { b, token, go };
  const byLink = hub.data.byLink && onToken ? { phoneEnd: hub.data.phoneEnd ?? "", token, onFull: onToken } : null;
  return (
    <ByLink.Provider value={byLink}>
      <div className="public my" style={style}>
        {asClient ? (
          <div className="my-viewing" role="status">
            <Icon name="lock" size={18} />
            <span>You are seeing what {b.firstName} sees. Read only.</span>
            <button type="button" onClick={onOut}>
              Back to client
            </button>
          </div>
        ) : null}
        <header className="my-top">
          <div className="my-top-in">
            <Logo url={b.logoUrl} name={b.name} className="my-logo" />
            {brands.length > 1 ? (
              <button type="button" className="my-switch" onClick={() => setSwitching(true)}>
                Switch
                <Icon name="chevronDown" size={16} />
              </button>
            ) : null}
          </div>
        </header>
        <div className="my-frame">
          <nav className="my-side" aria-label="My Trades">
            {SIDE.map((s) => (
              <button key={s.k} type="button" aria-current={page === s.k ? "page" : undefined} onClick={() => go(s.k)}>
                <Icon name={s.icon} size={20} />
                {s.label}
              </button>
            ))}
            <hr />
            <button type="button" onClick={onOut}>
              <Icon name={asClient ? "chevronLeft" : "signout"} size={20} />
              {asClient ? "Back to client" : "Log out"}
            </button>
          </nav>
          <main className="my-main">
            {page === "home" ? (
              <MyHome {...props} others={others} />
            ) : page === "visits" ? (
              <MyVisits {...props} />
            ) : page === "invoices" ? (
              <MyInvoices {...props} />
            ) : page === "services" ? (
              <MyServices {...props} others={others} />
            ) : page === "quotes" ? (
              <MyQuotes {...props} />
            ) : page === "requests" ? (
              <MyRequests {...props} />
            ) : page === "wallet" ? (
              <MyWallet {...props} />
            ) : page === "refer" ? (
              <MyRefer {...props} />
            ) : page === "contact" ? (
              <MyContact {...props} onOut={onOut} />
            ) : (
              <MyMore {...props} onOut={onOut} manyBrands={brands.length > 1} onSwitch={() => setSwitching(true)} />
            )}
          </main>
        </div>
        <div className="public-band my-foot" />
        <nav className="my-tabs" aria-label="My Trades">
          {TABS.map((t) => (
            <button key={t.k} type="button" aria-current={tab === t.k ? "page" : undefined} onClick={() => go(t.k)}>
              <Icon name={t.icon} size={22} />
              <span>{t.label}</span>
            </button>
          ))}
        </nav>
        <Sheet open={switching} title="Your businesses" onClose={() => setSwitching(false)}>
          <ul className="my-rows">
            {brands.map((x) => (
              <li key={x.companyId}>
                <button
                  type="button"
                  className="my-row"
                  aria-current={x.companyId === b.companyId ? "true" : undefined}
                  onClick={() => {
                    setPicked(x.companyId);
                    store.set(BRAND_KEY, x.companyId);
                    setSwitching(false);
                    go("home");
                  }}
                >
                  <BrandMark name={x.name} color={x.color} logoUrl={x.logoUrl} />
                  <span className="my-row-text">
                    <b>{x.name}</b>
                    <span>{x.next[0] ? `Next visit ${shortDate(x.next[0].date)}` : tradeOf(x.trade).pitch}</span>
                  </span>
                  {x.companyId === b.companyId ? <Icon name="check" size={20} /> : null}
                </button>
              </li>
            ))}
          </ul>
        </Sheet>
      </div>
    </ByLink.Provider>
  );
}

interface PageProps {
  b: PortalBrand;
  token: string;
  go: (p: Page) => void;
}

/** A sheet that slides up from the bottom on a phone and sits in the middle on a computer. */
function Sheet({
  open,
  title,
  onClose,
  changes,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  /** This sheet changes something (books, asks, saves): read only shows a note in its place. */
  changes?: boolean;
  children: ReactNode;
}) {
  const readOnly = useContext(ReadOnly);
  const byLink = useContext(ByLink);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.classList.add("my-locked");
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("my-locked");
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="my-scrim" onClick={onClose}>
      <div className="my-sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="my-sheet-head">
          <h2>{title}</h2>
          <button type="button" className="my-x" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>
        <div className="my-sheet-body">
          {changes && readOnly ? (
            <div className="my-stack">
              <Banner>The client does this here. You are viewing as them, so nothing is saved.</Banner>
              <Button size="large" fullWidth onClick={onClose}>
                OK
              </Button>
            </div>
          ) : changes && byLink ? (
            <CodeStep {...byLink} />
          ) : (
            children
          )}
        </div>
      </div>
    </div>
  );
}

/** Opened from a link: before a change, a code to the client's own phone proves it is them. */
function CodeStep({ phoneEnd, token, onFull }: { phoneEnd: string; token: string; onFull: (token: string) => void }) {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const head = { "x-portal-token": token };
  const go = async () => {
    setBusy(true);
    setErr(null);
    try {
      if (!sent) {
        await api("POST", "public/my/code", { origin: window.location.origin }, head);
        setSent(true);
      } else onFull((await api<{ token: string }>("POST", "public/my/verify", { code }, head)).token);
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="my-stack"
      onSubmit={(e) => {
        e.preventDefault();
        void go();
      }}
    >
      <p>
        {sent
          ? `Type the 6 digit code we sent to your phone ending in ${phoneEnd}.`
          : `Quick check to keep your account safe. We will text a code to your phone ending in ${phoneEnd}. Then you can make changes for 1 hour.`}
      </p>
      {sent ? (
        <label className="field">
          <span className="label">Code</span>
          <div className="input">
            <input
              className="my-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
          </div>
        </label>
      ) : null}
      {err ? <Banner type="error">{err}</Banner> : null}
      <Button size="large" fullWidth submit loading={busy} disabled={sent && code.length !== 6}>
        {sent ? "Continue" : "Text me a code"}
      </Button>
    </form>
  );
}

/** The white logo on a color band, or the name when there is none or it does not load. */
function Logo({ url, name, className }: { url: string | null; name: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <span className="my-name">{name}</span>;
  return <img className={className} src={url} alt={name} onError={() => setFailed(true)} />;
}

function BrandMark({ name, color, logoUrl }: { name: string; color: string | null; logoUrl: string | null }) {
  const letter = name
    .replace(/^the\s+/i, "")
    .charAt(0)
    .toUpperCase();
  return (
    <span className="my-mark" style={{ background: color ?? "#1f6f5c" }} aria-hidden>
      {logoUrl ? <img src={logoUrl} alt="" /> : letter}
    </span>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="my-section">
      <div className="my-section-head">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** The add-ons offered with a visit: every one, or the ones tied to the service it was priced from. */
const addonsFor = (b: PortalBrand, v: Visit) => b.addons.filter((a) => a.only === null || a.only === v.serviceId);
const addonWords = (b: PortalBrand, v: Visit) =>
  v.addons
    .map((x) => {
      const name = b.addons.find((a) => a.id === x.id)?.name ?? "Add-on";
      return x.count > 1 ? `${name} × ${String(x.count)}` : name;
    })
    .join(", ");
const isUpcoming = (v: Visit | PastVisit): v is Visit => "addons" in v;

function VisitCard({ v, b, onOpen }: { v: Visit | PastVisit; b: PortalBrand; onOpen: () => void }) {
  return (
    <button type="button" className="my-card my-tap" onClick={onOpen}>
      <span className="my-card-title">{longDay(v.date)}</span>
      <span className="my-meta">
        <Icon name="clock" size={18} />
        {arrival(v)}
      </span>
      {v.address ? (
        <span className="my-meta">
          <Icon name="pin" size={18} />
          {v.address}
        </span>
      ) : null}
      {isUpcoming(v) && v.addons.length ? (
        <span className="my-meta">
          <Icon name="sparkle" size={18} />
          {addonWords(b, v)}
        </span>
      ) : null}
    </button>
  );
}

function VisitSheet({
  v,
  b,
  onClose,
  onExtras,
}: {
  v: Visit | PastVisit | null;
  b: PortalBrand;
  onClose: () => void;
  onExtras: (v: Visit) => void;
}) {
  const extras = v && isUpcoming(v) && v.addonsOpen && addonsFor(b, v).length > 0;
  return (
    <Sheet open={Boolean(v)} title={v ? v.job : ""} onClose={onClose}>
      {v ? (
        <div className="my-stack">
          <p className="my-big">{longDay(v.date)}</p>
          <p className="my-meta">
            <Icon name="clock" size={18} />
            {arrival(v)}
          </p>
          {v.address ? (
            <a className="my-meta" href={mapLink(v.address)} target="_blank" rel="noopener noreferrer">
              <Icon name="pin" size={18} />
              {v.address}
            </a>
          ) : null}
          <p className="my-meta">
            <Icon name="person" size={18} />
            {"report" in v ? (v.crew.length ? `Done by ${v.crew.join(" and ")}` : "Done") : crewWords(v.crew)}
          </p>
          {isUpcoming(v) && v.addons.length ? (
            <p className="my-meta">
              <Icon name="sparkle" size={18} />
              Extras {addonWords(b, v)}
            </p>
          ) : null}
          {extras ? (
            <Button type="secondary" size="large" fullWidth icon="plus" onClick={() => onExtras(v)}>
              {v.addons.length ? "Change extras" : "Add extras to this visit"}
            </Button>
          ) : null}
          {"report" in v && v.report ? (
            <a className="btn primary full" href={v.report}>
              See your visit report
            </a>
          ) : null}
          {b.phone && !("report" in v) ? (
            <p className="muted small">
              Need to move this visit? <a href={`sms:${b.phone}`}>Text us</a> or <a href={`tel:${b.phone}`}>call</a>.
            </p>
          ) : null}
        </div>
      ) : null}
    </Sheet>
  );
}

function MyHome({ b, token, go, others }: PageProps & { others: PortalOther[] }) {
  const [open, setOpen] = useState<Visit | null>(null);
  const [extras, setExtras] = useState<Visit | null>(null);
  const [ask, setAsk] = useState<Target | null>(null);
  const [quote, setQuote] = useState<QuoteMode | null>(null);
  const [adding, setAdding] = useState(false);
  const next = b.next[0];
  const late = b.invoices.filter((i) => i.state === "past_due");
  const owed = b.invoices.filter((i) => i.state !== "paid");
  const owedCents = owed.reduce((s, i) => s + i.balanceCents, 0);
  const plan = b.plans[0];
  const myOffer = b.quotes.find((q) => q.offer);
  return (
    <>
      <h1 className="my-hi">Hi {b.firstName}</h1>
      {owed.length ? (
        <div className={`my-owe${late.length ? " late" : ""}`}>
          <div>
            <b>
              {late.length
                ? `You have ${late.length === 1 ? "1 past due invoice" : `${String(late.length)} past due invoices`}`
                : `${owed.length === 1 ? "1 invoice" : `${String(owed.length)} invoices`} to pay`}
            </b>
            <span>{moneyExact(owedCents)}</span>
          </div>
          {owed.length === 1 && owed[0] ? (
            <a className="btn primary" href={owed[0].payLink}>
              Pay now
            </a>
          ) : (
            <Button onClick={() => go("invoices")}>Pay now</Button>
          )}
        </div>
      ) : null}
      {b.results ? <MyResults r={b.results} /> : null}
      {next ? (
        <Section title="Next visit">
          <button type="button" className="my-next my-tap" onClick={() => setOpen(next)}>
            <span className="my-next-day">{longDay(next.date)}</span>
            <span className="my-next-time">{arrival(next)}</span>
            <span className="my-next-crew">
              <Icon name="person" size={18} />
              {crewWords(next.crew)}
            </span>
            {next.addons.length ? (
              <span className="my-next-crew">
                <Icon name="sparkle" size={18} />
                {addonWords(b, next)}
              </span>
            ) : null}
          </button>
          {next.addonsOpen && addonsFor(b, next).length ? (
            <Button type="secondary" fullWidth icon="plus" onClick={() => setExtras(next)}>
              {next.addons.length ? "Change extras" : "Add extras to this visit"}
            </Button>
          ) : null}
        </Section>
      ) : b.trade !== "agency" ? (
        <p className="muted">No visit on the schedule yet.</p>
      ) : null}
      {myOffer?.offer ? (
        // The plan we offered them, kept here until they book it (owner, 2026-11-02).
        <section className="my-keep">
          <h2>Your offer</h2>
          <p className="muted">Keep your home this clean on a schedule. Pick your day, or let us pick the soonest one.</p>
          <ul className="my-keep-prices">
            <li>
              <span>{myOffer.offer.often}</span>
              <b className="num">{moneyExact(myOffer.offer.perVisitCents)} a visit</b>
            </li>
          </ul>
          <a className="btn primary large full" href={myOffer.link}>
            Pick your day
          </a>
        </section>
      ) : b.planOffer ? (
        <section className="my-keep">
          <h2>Keep it this clean</h2>
          <p className="muted">Come back on a schedule and your home stays like this.</p>
          <ul className="my-keep-prices">
            {b.planOffer.prices.map((x) => (
              <li key={x.id}>
                <span>{x.label}</span>
                <b className="num">{moneyExact(x.perVisitCents)} a visit</b>
              </li>
            ))}
          </ul>
          <Button size="large" fullWidth onClick={() => setQuote("regular")}>
            Start my schedule
          </Button>
        </section>
      ) : null}
      <div className="my-quick">
        {plan ? <Quick icon="plusCircle" label="Add a visit" onClick={() => setAdding(true)} /> : null}
        <Quick icon="requests" label="Get a quote" onClick={() => (b.quoter ? setQuote("any") : setAsk({ kind: "own", b }))} />
        <Quick icon="invoices" label="Invoices" onClick={() => go("invoices")} />
        {b.phone ? (
          <Quick icon="message" label="Text us" href={`sms:${b.phone}`} />
        ) : (
          <Quick icon="phone" label="Contact us" onClick={() => go("contact")} />
        )}
      </div>
      {b.next.length > 1 ? (
        <Section
          title="Coming up"
          action={
            <button type="button" className="my-link" onClick={() => go("visits")}>
              See all
            </button>
          }
        >
          <div className="my-cards">
            {b.next.slice(1, 3).map((v) => (
              <VisitCard key={v.ref} v={v} b={b} onOpen={() => setOpen(v)} />
            ))}
          </div>
        </Section>
      ) : null}
      {others.length ? (
        <Section title="More from our family">
          <div className="my-scroller">
            {others.map((o) => (
              <OtherCard key={o.brandId} o={o} onAsk={() => setAsk({ kind: "other", o })} />
            ))}
          </div>
        </Section>
      ) : null}
      {b.refer ? (
        <button type="button" className="my-refer my-tap" onClick={() => go("refer")}>
          <b>
            Give {money(b.refer.amountCents)}, get {money(b.refer.amountCents)}
          </b>
          <span>Share your link with a friend</span>
          <Icon name="chevronRight" size={20} />
        </button>
      ) : null}
      <VisitSheet
        v={open}
        b={b}
        onClose={() => setOpen(null)}
        onExtras={(v) => {
          setOpen(null);
          setExtras(v);
        }}
      />
      <AddonsSheet v={extras} b={b} token={token} onClose={() => setExtras(null)} />
      {plan ? <AddVisit b={b} plans={b.plans} token={token} open={adding} onClose={() => setAdding(false)} /> : null}
      <RequestSheet target={ask} b={b} token={token} onClose={() => setAsk(null)} />
      <QuoteSheet
        b={b}
        token={token}
        mode={quote}
        onClose={() => setQuote(null)}
        onOther={() => {
          setQuote(null);
          setAsk({ kind: "own", b });
        }}
      />
    </>
  );
}

function Quick({ icon, label, onClick, href }: { icon: IconName; label: string; onClick?: () => void; href?: string }) {
  const inner = (
    <>
      <span className="my-quick-icon">
        <Icon name={icon} size={22} />
      </span>
      {label}
    </>
  );
  return href ? (
    <a className="my-quick-btn" href={href}>
      {inner}
    </a>
  ) : (
    <button type="button" className="my-quick-btn" onClick={onClick}>
      {inner}
    </button>
  );
}

function OtherCard({ o, onAsk }: { o: PortalOther; onAsk: () => void }) {
  return (
    <div className="my-other" style={{ "--other": o.color ?? "#1f6f5c" } as CSSProperties}>
      <div className="my-other-top">
        <Logo url={o.logoUrl} name={o.name} />
      </div>
      <div className="my-other-body">
        <span>{tradeOf(o.trade).pitch}</span>
        <button type="button" className="btn secondary full" onClick={onAsk}>
          Get a quote
        </button>
      </div>
    </div>
  );
}

/** A marketing client's results this month so far (owner answer 119). Plain words; the client reads them. */
function MyResults({ r }: { r: NonNullable<PortalBrand["results"]> }) {
  const rows: [string, string][] = [];
  if (r.leadsNow !== null)
    rows.push([
      "Leads this month",
      r.leadsBefore !== null ? `${String(r.leadsNow)} (same days last month ${String(r.leadsBefore)})` : String(r.leadsNow),
    ]);
  if (r.costPerLeadCents !== null) rows.push(["Cost per lead", moneyExact(r.costPerLeadCents)]);
  if (r.reviews !== null)
    rows.push(["Google reviews", `${String(r.reviews)}${r.rating !== null ? `, ${String(r.rating)} stars` : ""}`]);
  if (r.rankNow !== null)
    rows.push([
      "Average map place",
      r.rankBefore !== null ? `${r.rankNow.toFixed(1)} (before ${r.rankBefore.toFixed(1)})` : r.rankNow.toFixed(1),
    ]);
  return (
    <Section title="Your marketing results">
      <div className="my-card">
        {rows.length ? (
          <dl className="my-results">
            {rows.map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd className="num">{v}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p>We are still gathering your numbers. They show here soon.</p>
        )}
        {r.planCents ? (
          <p className="muted small">Your plan is {moneyExact(r.planCents)} a month. A lower map place is better.</p>
        ) : null}
      </div>
    </Section>
  );
}

function AddVisit({
  b,
  plans,
  token,
  open,
  onClose,
}: {
  b: PortalBrand;
  plans: PortalBrand["plans"];
  token: string;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [jobId, setJobId] = useState(plans[0]?.jobId ?? "");
  const [day, setDay] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const plan = plans.find((p) => p.jobId === jobId) ?? plans[0];
  const readOnly = useLocked();
  const days = useQuery({
    queryKey: ["my", "days", b.companyId],
    queryFn: () =>
      api<{ days: string[] }>("GET", `public/my/open-days?companyId=${b.companyId}`, undefined, { "x-portal-token": token }),
    enabled: open && !readOnly,
  });
  const close = () => {
    onClose();
    setMsg(null);
    setDay("");
  };
  const book = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      const r = await api<{ waiting: boolean }>(
        "POST",
        "public/my/visits",
        { companyId: b.companyId, jobId: plan.jobId, date: day },
        { "x-portal-token": token },
      );
      setMsg({
        ok: true,
        text: r.waiting
          ? `We got your ask for ${longDay(day)}. We will check the schedule and text you soon.`
          : `You are booked for ${longDay(day)} at ${moneyExact(plan.priceCents)}.`,
      });
      await qc.invalidateQueries({ queryKey: ["my"] });
    } catch (e) {
      setMsg({ ok: false, text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} title="Add a visit" onClose={close} changes>
      {msg?.ok ? (
        <div className="my-stack">
          <Banner type="success">{msg.text}</Banner>
          <Button size="large" fullWidth onClick={close}>
            Done
          </Button>
        </div>
      ) : plan ? (
        <div className="my-stack">
          {plans.length > 1 ? (
            <div className="my-chips">
              {plans.map((p) => (
                <button key={p.jobId} type="button" aria-pressed={p.jobId === plan.jobId} onClick={() => setJobId(p.jobId)}>
                  {p.job}
                </button>
              ))}
            </div>
          ) : null}
          <p>
            One more {tradeOf(b.trade).visit} of your <b>{plan.job}</b> at your plan price, <b>{moneyExact(plan.priceCents)}</b>.
          </p>
          <p className="my-label">Pick a day</p>
          {days.isLoading ? (
            <Skeleton rows={2} />
          ) : days.data?.days.length ? (
            <div className="my-days">
              {days.data.days.slice(0, 21).map((d) => (
                <button key={d} type="button" aria-pressed={d === day} onClick={() => setDay(d)}>
                  <span>{weekdayName(d).slice(0, 3)}</span>
                  <b>{shortDate(d)}</b>
                </button>
              ))}
            </div>
          ) : (
            <p className="muted">No open days right now. Text us and we will fit you in.</p>
          )}
          {msg ? <Banner type="error">{msg.text}</Banner> : null}
          <Button size="large" fullWidth disabled={!day} loading={busy} onClick={() => void book()}>
            {day ? `Book ${shortDate(day)}` : "Book this day"}
          </Button>
        </div>
      ) : null}
    </Sheet>
  );
}

/**
 * Add extras: the price list's add-ons on one upcoming visit, until the day before it. A yes or no add-on is a tap; one
 * priced each has a count. Saving puts them on that visit's bill; taking one off takes it off.
 */
function AddonsSheet({ v, b, token, onClose }: { v: Visit | null; b: PortalBrand; token: string; onClose: () => void }) {
  // Keyed by the visit: the picks start from what is on that visit each time it opens.
  return v ? <AddonsForm key={v.ref} v={v} b={b} token={token} onClose={onClose} /> : null;
}

function AddonsForm({ v, b, token, onClose }: { v: Visit; b: PortalBrand; token: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [picks, setPicks] = useState<Record<string, number>>(() => Object.fromEntries(v.addons.map((x) => [x.id, x.count])));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // A plan visit gets each add-on's plan price, when it has one (owner, 2026-11-02).
  const offered = addonsFor(b, v).map((a) =>
    v.onPlan && a.planCents !== null && a.planCents !== a.cents
      ? { ...a, cents: a.planCents, listCents: a.cents }
      : { ...a, listCents: null },
  );
  const totalCents = offered.reduce((s, a) => s + a.cents * (picks[a.id] ?? 0), 0);
  const set = (id: string, n: number) =>
    setPicks((p) => ({
      ...Object.fromEntries(Object.entries(p).filter(([k]) => k !== id)),
      ...(n > 0 ? { [id]: Math.min(n, 20) } : {}),
    }));
  const save = async () => {
    setBusy(true);
    try {
      await api(
        "PUT",
        "public/my/visit-addons",
        { companyId: b.companyId, ref: v.ref, addons: picks },
        { "x-portal-token": token },
      );
      const n = Object.keys(picks).length;
      setMsg({
        ok: true,
        text: n
          ? `Your extras are on your ${tradeOf(b.trade).visit} for ${longDay(v.date)}. They add ${moneyExact(totalCents)} to that bill.`
          : `Your ${tradeOf(b.trade).visit} for ${longDay(v.date)} has no extras now.`,
      });
      await qc.invalidateQueries({ queryKey: ["my"] });
    } catch (e) {
      setMsg({ ok: false, text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open title="Add extras" onClose={onClose} changes>
      {msg?.ok ? (
        <div className="my-stack">
          <Banner type="success">{msg.text}</Banner>
          <Button size="large" fullWidth onClick={onClose}>
            Done
          </Button>
        </div>
      ) : (
        <div className="my-stack">
          <p>
            Pick extras for your {tradeOf(b.trade).visit} on <b>{longDay(v.date)}</b>. You can change them until the day before.
          </p>
          <ul className="my-addons">
            {offered.map((a) => {
              const n = picks[a.id] ?? 0;
              return (
                <li key={a.id} className={n ? "on" : undefined}>
                  <div className="my-addon-text">
                    <b>{a.name}</b>
                    <span className="num">
                      {moneyExact(a.cents)}
                      {a.each ? " each" : ""}
                      {a.listCents !== null ? `, your plan price (usually ${moneyExact(a.listCents)})` : ""}
                    </span>
                    {a.about ? <span className="muted small">{a.about}</span> : null}
                  </div>
                  {a.each ? (
                    <div className="my-count">
                      <button type="button" aria-label={`Fewer ${a.name}`} disabled={!n} onClick={() => set(a.id, n - 1)}>
                        −
                      </button>
                      <span className="num" aria-live="polite">
                        {n}
                      </span>
                      <button type="button" aria-label={`More ${a.name}`} disabled={n >= 20} onClick={() => set(a.id, n + 1)}>
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="my-addon-pick"
                      aria-pressed={n > 0}
                      aria-label={`${n ? "Remove" : "Add"} ${a.name}`}
                      onClick={() => set(a.id, n ? 0 : 1)}
                    >
                      <Icon name={n ? "check" : "plus"} size={20} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="my-addons-total">
            <span>Extras</span>
            <b className="num">{moneyExact(totalCents)}</b>
          </p>
          {msg ? <Banner type="error">{msg.text}</Banner> : null}
          <Button size="large" fullWidth loading={busy} onClick={() => void save()}>
            {v.addons.length || Object.keys(picks).length ? "Save my extras" : "Save"}
          </Button>
          <p className="muted small">Extras are billed with this visit only. Your usual price stays the same.</p>
        </div>
      )}
    </Sheet>
  );
}

type Target = { kind: "own"; b: PortalBrand } | { kind: "other"; o: PortalOther };

/** Get a quote: more work from this brand, or a sister brand they do not use yet. Lands as a request at the office. */
function RequestSheet({
  target,
  b,
  token,
  onClose,
}: {
  target: Target | null;
  b: PortalBrand;
  token: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [what, setWhat] = useState("");
  const [day, setDay] = useState("");
  const [other, setOther] = useState(false);
  const [place, setPlace] = useState<Place>(NO_PLACE);
  const [photos, setPhotos] = useState<Shot[]>([]);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ business: string; missed: number } | null>(null);
  const name = target ? (target.kind === "own" ? target.b.name : target.o.name) : "";
  const trade = target ? (target.kind === "own" ? target.b.trade : target.o.trade) : "";
  // A sister brand with a ZIP list checks the ZIP before the request goes (owner, 2026-10-30); with none on file, ask for it.
  const needZip = target?.kind === "other" && target.o.needsZip;
  const typing = other || !b.address || needZip;
  // Typed addresses are saved in parts (owner rule): all but Street 2 are needed.
  const placeOk =
    !typing ||
    (place.street1.trim() !== "" &&
      place.city.trim() !== "" &&
      /^[A-Za-z]{2}$/.test(place.state.trim()) &&
      /^\d{5}$/.test(place.zip.trim()));
  const close = () => {
    onClose();
    setWhat("");
    setDay("");
    setOther(false);
    setPlace(NO_PLACE);
    setPhotos([]);
    setErr(null);
    setStep(null);
    setDone(null);
  };
  const addPhotos = async (list: FileList | null) => {
    if (!list) return;
    setErr(null);
    const room = REQUEST_PHOTOS_MAX - photos.length;
    const picked = [...list].filter((f) => f.type.startsWith("image/")).slice(0, room);
    if (list.length > room) setErr(`You can add up to ${String(REQUEST_PHOTOS_MAX)} photos.`);
    const made: Shot[] = [];
    for (const f of picked) {
      const shot = await shrink(f);
      if (shot) made.push(shot);
      else setErr("One photo didn't open. Try another one.");
    }
    setPhotos((p) => [...p, ...made].slice(0, REQUEST_PHOTOS_MAX));
  };
  const send = async () => {
    if (!target) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ business: string; id: string; companyId: string }>(
        "POST",
        "public/my/request",
        {
          ...(target.kind === "own" ? { companyId: target.b.companyId } : { brandId: target.o.brandId }),
          what,
          ...(day ? { day } : {}),
          ...(typing
            ? {
                place: {
                  street1: place.street1.trim(),
                  ...(place.street2.trim() ? { street2: place.street2.trim() } : {}),
                  city: place.city.trim(),
                  state: place.state.trim().toUpperCase(),
                  zip: place.zip.trim(),
                },
              }
            : {}),
        },
        { "x-portal-token": token },
      );
      // Photos go one at a time after the request, so each call stays small.
      let missed = 0;
      for (const [i, p] of photos.entries()) {
        setStep(`Sending photo ${String(i + 1)} of ${String(photos.length)}`);
        await api(
          "POST",
          "public/my/request-photo",
          { companyId: r.companyId, requestId: r.id, name: p.name, mime: "image/jpeg", dataBase64: p.base64 },
          { "x-portal-token": token },
        ).catch(() => {
          missed += 1;
        });
      }
      setDone({ business: r.business || name, missed });
      await qc.invalidateQueries({ queryKey: ["my"] });
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
      setStep(null);
    }
  };
  const pick = (p: string) => setWhat((w) => (w.includes(p) ? w : w.trim() ? `${w.trim()}, ${p.toLowerCase()}` : p));
  const set = (k: keyof Place) => (e: { target: { value: string } }) => setPlace((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Sheet open={Boolean(target)} title={`Get a quote from ${name}`} onClose={close} changes>
      {done ? (
        <div className="my-stack">
          <Banner type="success">Sent. {done.business} will reach out soon with your quote.</Banner>
          {done.missed ? (
            <Banner type="warning">
              {done.missed === 1 ? "1 photo" : `${String(done.missed)} photos`} didn't go. Text it to us and we will add it.
            </Banner>
          ) : null}
          <Button size="large" fullWidth onClick={close}>
            Done
          </Button>
        </div>
      ) : (
        <form
          className="my-stack"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          {tradeOf(trade).picks.length ? (
            <div className="my-chips">
              {tradeOf(trade).picks.map((p) => (
                <button
                  key={p}
                  type="button"
                  aria-pressed={what.includes(p) || what.includes(p.toLowerCase())}
                  onClick={() => pick(p)}
                >
                  {p}
                </button>
              ))}
            </div>
          ) : null}
          <label className="field">
            <span className="label">What do you need?</span>
            <textarea rows={3} maxLength={600} value={what} onChange={(e) => setWhat(e.target.value)} />
          </label>
          <div className="field">
            <span className="label">Photos (you can skip this)</span>
            <p className="muted small">A few pictures help us give you a price without a visit.</p>
            <div className="my-shots">
              {photos.map((p, i) => (
                <span key={p.url} className="my-shot">
                  <img src={p.url} alt={`Photo ${String(i + 1)}`} />
                  <button
                    type="button"
                    className="my-shot-x"
                    aria-label={`Remove photo ${String(i + 1)}`}
                    onClick={() => setPhotos((all) => all.filter((x) => x !== p))}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </span>
              ))}
              {photos.length < REQUEST_PHOTOS_MAX ? (
                <label className="my-shot my-shot-add">
                  <Icon name="camera" size={22} />
                  <span>Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      void addPhotos(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              ) : null}
            </div>
          </div>
          <label className="field">
            <span className="label">A day that works (you can skip this)</span>
            <div className="input">
              <input type="date" min={tomorrow()} value={day} onChange={(e) => setDay(e.target.value)} />
            </div>
          </label>
          {typing ? (
            <fieldset className="my-place">
              <legend className="label">{needZip ? "Your address, so we can check we work there" : "Address"}</legend>
              <label className="field">
                <span className="label">Street</span>
                <div className="input">
                  <input autoComplete="address-line1" value={place.street1} onChange={set("street1")} />
                </div>
              </label>
              <label className="field">
                <span className="label">Apt or suite (you can skip this)</span>
                <div className="input">
                  <input autoComplete="address-line2" value={place.street2} onChange={set("street2")} />
                </div>
              </label>
              <label className="field">
                <span className="label">City</span>
                <div className="input">
                  <input autoComplete="address-level2" value={place.city} onChange={set("city")} />
                </div>
              </label>
              <div className="my-place-row">
                <label className="field">
                  <span className="label">State</span>
                  <div className="input">
                    <input
                      autoComplete="address-level1"
                      maxLength={2}
                      placeholder="LA"
                      value={place.state}
                      onChange={set("state")}
                    />
                  </div>
                </label>
                <label className="field">
                  <span className="label">ZIP</span>
                  <div className="input">
                    <input autoComplete="postal-code" inputMode="numeric" maxLength={5} value={place.zip} onChange={set("zip")} />
                  </div>
                </label>
              </div>
            </fieldset>
          ) : (
            <p className="my-meta">
              <Icon name="pin" size={18} />
              <span>
                At {b.address}.{" "}
                <button type="button" className="my-link" onClick={() => setOther(true)}>
                  Another address
                </button>
              </span>
            </p>
          )}
          {err ? <Banner type="error">{err}</Banner> : null}
          {needZip ? <p className="muted small">We check your ZIP code to make sure we work in your area.</p> : null}
          <Button size="large" fullWidth submit loading={busy} disabled={what.trim().length < 3 || !placeOk}>
            {step ?? "Send my request"}
          </Button>
          <p className="muted small">No charge for a quote. We text you back, usually the same day.</p>
        </form>
      )}
    </Sheet>
  );
}

interface Place {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
}
const NO_PLACE: Place = { street1: "", street2: "", city: "", state: "", zip: "" };

/** A photo picked for a request, made small enough to send (one call each, under the size limit). */
interface Shot {
  name: string;
  url: string;
  base64: string;
}

/** Shrinks a phone photo to a JPEG at most 1600 pixels wide or tall (smaller again if still too big). Null if it won't open. */
async function shrink(file: File): Promise<Shot | null> {
  try {
    const img = await createImageBitmap(file);
    for (const [side, quality] of [
      [1600, 0.8],
      [1200, 0.7],
      [900, 0.6],
    ] as const) {
      const scale = Math.min(1, side / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
      const url = canvas.toDataURL("image/jpeg", quality);
      const base64 = url.slice(url.indexOf(",") + 1);
      if (base64.length <= 950_000) return { name: file.name.replace(/\.\w+$/, "") + ".jpg", url, base64 };
    }
    return null;
  } catch {
    return null;
  }
}

function MyVisits({ b, token }: PageProps) {
  const [view, setView] = useState<"up" | "past">("up");
  const [open, setOpen] = useState<Visit | PastVisit | null>(null);
  const [extras, setExtras] = useState<Visit | null>(null);
  const [adding, setAdding] = useState(false);
  const [shown, setShown] = useState(5);
  const list = view === "up" ? b.next : b.past;
  return (
    <>
      <div className="my-title">
        <h1>Your visits</h1>
        {b.plans.length ? (
          <Button type="secondary" icon="plus" onClick={() => setAdding(true)}>
            Add a visit
          </Button>
        ) : null}
      </div>
      <div className="my-seg" role="tablist">
        <button type="button" role="tab" aria-selected={view === "up"} onClick={() => setView("up")}>
          Upcoming
        </button>
        <button type="button" role="tab" aria-selected={view === "past"} onClick={() => setView("past")}>
          Past
        </button>
      </div>
      {list.length ? (
        <div className="my-cards">
          {list.slice(0, shown).map((v) => (
            <VisitCard key={v.ref} v={v} b={b} onOpen={() => setOpen(v)} />
          ))}
          {list.length > shown ? (
            <Button type="tertiary" fullWidth onClick={() => setShown((n) => n + 10)}>
              Show more
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="muted">{view === "up" ? "Nothing on the schedule yet." : "No visits yet."}</p>
      )}
      <VisitSheet
        v={open}
        b={b}
        onClose={() => setOpen(null)}
        onExtras={(v) => {
          setOpen(null);
          setExtras(v);
        }}
      />
      <AddonsSheet v={extras} b={b} token={token} onClose={() => setExtras(null)} />
      {b.plans.length ? <AddVisit b={b} plans={b.plans} token={token} open={adding} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function InvoiceCard({ i }: { i: Invoice }) {
  return (
    <a className={`my-card my-tap${i.state === "past_due" ? " late" : ""}`} href={i.payLink}>
      <span className="my-card-row">
        <span className="my-card-title">{i.title}</span>
        <span className="muted small">{i.number ? `#${String(i.number)}` : ""}</span>
      </span>
      <span className="my-meta">
        <Icon name="schedule" size={18} />
        {[
          i.issuedOn ? `Sent ${shortDate(i.issuedOn)}` : null,
          i.paidOn ? `Paid ${shortDate(i.paidOn)}` : i.dueOn ? `Due ${shortDate(i.dueOn)}` : null,
        ]
          .filter(Boolean)
          .join(" | ")}
      </span>
      <span className="my-card-foot">
        <span className={`my-pill ${i.state}`}>
          {i.state === "paid" ? "Paid" : i.state === "past_due" ? "Past due" : "To pay"}
        </span>
        <span className="my-total">
          <span>{i.state === "paid" ? "Total" : "Balance"}</span>
          <b>{moneyExact(i.state === "paid" ? i.totalCents : i.balanceCents)}</b>
        </span>
      </span>
    </a>
  );
}

function MyInvoices({ b }: PageProps) {
  const [shown, setShown] = useState(6);
  const groups: [string, Invoice[], string][] = [
    ["Past due", b.invoices.filter((i) => i.state === "past_due"), "late"],
    ["To pay", b.invoices.filter((i) => i.state === "open"), ""],
    ["Paid", b.invoices.filter((i) => i.state === "paid").slice(0, shown), "paid"],
  ];
  const paid = b.invoices.filter((i) => i.state === "paid").length;
  return (
    <>
      <h1>Your invoices</h1>
      {b.invoices.length ? (
        groups.map(([title, list, cls]) =>
          list.length ? (
            <section key={title} className={`my-section ${cls}`}>
              <h2 className="my-group">{title}</h2>
              <div className="my-cards">
                {list.map((i) => (
                  <InvoiceCard key={i.payLink} i={i} />
                ))}
              </div>
            </section>
          ) : null,
        )
      ) : (
        <p className="muted">No invoices yet.</p>
      )}
      {paid > shown ? (
        <Button type="tertiary" fullWidth onClick={() => setShown((n) => n + 10)}>
          Show more
        </Button>
      ) : null}
    </>
  );
}

function MyServices({ b, token, others }: PageProps & { others: PortalOther[] }) {
  const [ask, setAsk] = useState<Target | null>(null);
  const [quote, setQuote] = useState<QuoteMode | null>(null);
  return (
    <>
      <h1>Get a quote</h1>
      <Section title={`From ${b.name}`}>
        <button
          type="button"
          className="my-card my-tap my-own"
          onClick={() => (b.quoter ? setQuote("any") : setAsk({ kind: "own", b }))}
        >
          <span className="my-card-row">
            <span className="my-card-title">Get a quote from {b.name}</span>
            <Icon name="chevronRight" size={20} />
          </span>
          <span className="muted">{tradeOf(b.trade).picks.slice(0, 3).join(", ")} and more.</span>
        </button>
      </Section>
      {others.length ? (
        <Section title="Our other trades">
          <p className="muted small">Same family, same easy booking and billing, all in this one place.</p>
          <div className="my-others">
            {others.map((o) => (
              <OtherCard key={o.brandId} o={o} onAsk={() => setAsk({ kind: "other", o })} />
            ))}
          </div>
        </Section>
      ) : null}
      <RequestSheet target={ask} b={b} token={token} onClose={() => setAsk(null)} />
      <QuoteSheet
        b={b}
        token={token}
        mode={quote}
        onClose={() => setQuote(null)}
        onOther={() => {
          setQuote(null);
          setAsk({ kind: "own", b });
        }}
      />
    </>
  );
}

const QUOTE_WORDS: Record<string, string> = { sent: "Waiting for your OK", approved: "Approved", converted: "Booked" };
/**
 * Offers first (owner, 2026-11-02): a plan we offered stays here until they book it or it gets old, so they can come
 * back to it after they leave the page. Then their quotes.
 */
function MyQuotes({ b }: PageProps) {
  const offers = b.quotes.filter((q) => q.offer);
  const quotes = b.quotes.filter((q) => !q.offer);
  return (
    <>
      {offers.length ? (
        <>
          <h1>Offers</h1>
          <div className="my-cards">
            {offers.map((q) => (
              <a key={q.link} className="my-card my-tap" href={q.link}>
                <span className="my-card-row">
                  <span className="my-card-title">{q.offer!.often}</span>
                  <b>{moneyExact(q.offer!.perVisitCents)} a visit</b>
                </span>
                <span className="my-meta">Pick your schedule and day, or let us pick the soonest day.</span>
                <span className="my-card-foot">
                  <span className="my-pill open">Open</span>
                  <span className="link">Pick your day</span>
                </span>
              </a>
            ))}
          </div>
        </>
      ) : null}
      <h1>Your quotes</h1>
      {quotes.length ? (
        <div className="my-cards">
          {quotes.map((q) => (
            <a key={q.link} className="my-card my-tap" href={q.link}>
              <span className="my-card-row">
                <span className="my-card-title">{q.title ?? "Quote"}</span>
                <span className="muted small">#{q.number}</span>
              </span>
              {q.sentOn ? <span className="my-meta">Sent {shortDate(q.sentOn)}</span> : null}
              <span className="my-card-foot">
                <span className={`my-pill ${q.status === "sent" ? "open" : "paid"}`}>{QUOTE_WORDS[q.status] ?? q.status}</span>
                <span className="my-total">
                  <span>Total</span>
                  <b>{moneyExact(q.totalCents)}</b>
                </span>
              </span>
            </a>
          ))}
        </div>
      ) : (
        <p className="muted">No quotes yet.</p>
      )}
    </>
  );
}

const REQUEST_WORDS: Record<string, string> = { new: "We got it", sent: "Quote sent", converted: "Booked", lost: "Closed" };
/** What happens next, in their words, at the top of an open request. */
const REQUEST_NEXT: Record<string, string> = {
  new: "We text you back with your quote, usually the same day.",
  sent: "Your quote is ready. Take a look and say yes when it works for you.",
  converted: "You are booked. Your visits show under Visits.",
  lost: "This one is closed. Send a new request any time.",
};
type MyRequest = PortalBrand["requests"][number];

/**
 * Book or ask (was Requests, owner 2026-11-02): another cleaning, a regular schedule, or something specific, then the
 * asks they sent before. Moving or skipping a visit stays in Visits.
 */
function MyRequests({ b, token, go }: PageProps) {
  const [asking, setAsking] = useState(false);
  const [quote, setQuote] = useState<QuoteMode | null>(null);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<MyRequest | null>(null);
  const plan = b.plans.length > 0;
  const quoteOr = (mode: QuoteMode) => (b.quoter ? setQuote(mode) : setAsking(true));
  return (
    <>
      <h1>Book or ask</h1>
      <div className="my-asks">
        <button type="button" className="my-card my-tap" onClick={() => (plan ? setAdding(true) : quoteOr("once"))}>
          <span className="my-card-row">
            <span className="my-card-title">Add a cleaning</span>
            <Icon name="chevronRight" size={20} />
          </span>
          <span className="muted">{plan ? "One more visit at your plan price." : "One more clean, priced right here."}</span>
        </button>
        {plan ? null : (
          <button type="button" className="my-card my-tap" onClick={() => quoteOr("regular")}>
            <span className="my-card-row">
              <span className="my-card-title">Start a regular schedule</span>
              <Icon name="chevronRight" size={20} />
            </span>
            <span className="muted">Every week, every 2 weeks or once a month. See your price and pick your first day.</span>
          </button>
        )}
        <button type="button" className="my-card my-tap" onClick={() => setAsking(true)}>
          <span className="my-card-row">
            <span className="my-card-title">Something specific</span>
            <Icon name="chevronRight" size={20} />
          </span>
          <span className="muted">A task or a look at something. Add photos and we will price it.</span>
        </button>
      </div>
      <h2 className="my-asks-title">Your requests</h2>
      {b.requests.length ? (
        <ul className="my-rows">
          {b.requests.map((r) => (
            <li key={r.id}>
              <button type="button" className="my-row" onClick={() => setOpen(r)}>
                <span className="my-row-text">
                  <b>{r.title}</b>
                  <span>Asked {shortDate(r.askedOn)}</span>
                </span>
                <span className={`my-pill ${r.state === "converted" ? "paid" : r.state === "lost" ? "" : "open"}`}>
                  {REQUEST_WORDS[r.state]}
                </span>
                <Icon name="chevronRight" size={20} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">No requests yet.</p>
      )}
      <Sheet open={open !== null} title={open?.title ?? ""} onClose={() => setOpen(null)}>
        {open ? (
          <div className="my-stack">
            <p className="my-req-state">
              <span className={`my-pill ${open.state === "converted" ? "paid" : open.state === "lost" ? "" : "open"}`}>
                {REQUEST_WORDS[open.state]}
              </span>
              <span>{REQUEST_NEXT[open.state]}</span>
            </p>
            <dl className="my-req">
              <div>
                <dt>Asked</dt>
                <dd>{shortDate(open.askedOn)}</dd>
              </div>
              {open.what ? (
                <div>
                  <dt>What you asked for</dt>
                  <dd>{open.what}</dd>
                </div>
              ) : null}
              {open.wantsOn ? (
                <div>
                  <dt>Day you picked</dt>
                  <dd>{shortDate(open.wantsOn)}</dd>
                </div>
              ) : null}
              {open.address ? (
                <div>
                  <dt>Where</dt>
                  <dd>{open.address}</dd>
                </div>
              ) : null}
              {open.photos ? (
                <div>
                  <dt>Photos</dt>
                  <dd>{open.photos === 1 ? "1 photo sent" : `${String(open.photos)} photos sent`}</dd>
                </div>
              ) : null}
            </dl>
            {open.quote ? (
              <a className="btn primary large full" href={open.quote.link}>
                See your quote #{open.quote.number}
              </a>
            ) : null}
            {open.state === "converted" ? (
              <Button
                type="secondary"
                size="large"
                fullWidth
                onClick={() => {
                  setOpen(null);
                  go("visits");
                }}
              >
                See your visits
              </Button>
            ) : null}
          </div>
        ) : null}
      </Sheet>
      <RequestSheet target={asking ? { kind: "own", b } : null} b={b} token={token} onClose={() => setAsking(false)} />
      <QuoteSheet
        b={b}
        token={token}
        mode={quote}
        onClose={() => setQuote(null)}
        onOther={() => {
          setQuote(null);
          setAsking(true);
        }}
      />
      {plan ? <AddVisit b={b} plans={b.plans} token={token} open={adding} onClose={() => setAdding(false)} /> : null}
    </>
  );
}

/**
 * Get a quote on the quoter (owner, 2026-11-02, vault project-hub-booking): pick the clean, the home and how often, the
 * price shows at once, and Book it opens the quote page to pick the day. Every 2 weeks is picked first for a clean that
 * repeats, One time beside it. "regular" opens on the plain repeating clean (Start a regular schedule, Keep it this
 * clean); "once" on a one time clean (Add a cleaning, with no plan yet). Something else goes to the request form.
 */
type QuoteMode = "any" | "regular" | "once";

function QuoteSheet({
  b,
  token,
  mode,
  onClose,
  onOther,
}: {
  b: PortalBrand;
  token: string;
  mode: QuoteMode | null;
  onClose: () => void;
  onOther: () => void;
}) {
  const readOnly = useLocked();
  const setup = useQuery({
    queryKey: ["my", "quoter", b.companyId],
    queryFn: () =>
      api<HubQuoterAnswer>("GET", `public/my/quoter?companyId=${b.companyId}`, undefined, { "x-portal-token": token }),
    enabled: mode !== null && !readOnly,
  });
  return (
    <Sheet open={mode !== null} title={`Get a quote from ${b.name}`} onClose={onClose} changes>
      {setup.data && mode ? (
        <Quoter key={mode} b={b} token={token} s={setup.data} mode={mode} onOther={onOther} />
      ) : setup.error ? (
        <Banner type="error">{errText(setup.error)}</Banner>
      ) : (
        <Skeleton rows={4} />
      )}
    </Sheet>
  );
}

function Quoter({
  b,
  token,
  s,
  mode,
  onOther,
}: {
  b: PortalBrand;
  token: string;
  s: HubQuoterAnswer;
  mode: QuoteMode;
  onOther: () => void;
}) {
  const firstId = mode === "regular" ? (s.regularId ?? s.home.serviceId) : s.home.serviceId;
  const repeatsOf = (id: string) => s.services.find((x) => x.id === id)?.repeats ?? false;
  const startOften = (id: string): HomeAnswers["often"] =>
    mode !== "once" && repeatsOf(id) && s.repeats.some((r) => r.id === HUB_DEFAULT_OFTEN) ? HUB_DEFAULT_OFTEN : "once";
  const [hourly, setHourly] = useState(false);
  const [home, setHome] = useState<HomeAnswers>({ ...s.home, serviceId: firstId, often: startOften(firstId), extras: {} });
  const [byHour, setByHour] = useState<HourlyAnswers>({ cleaners: 1, hours: 2, often: HUB_DEFAULT_OFTEN });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const answers = hourly ? { kind: "hourly" as const, ...byHour } : home;
  const key = JSON.stringify(answers);
  const price = useQuery({
    queryKey: ["my", "price", b.companyId, key],
    queryFn: () =>
      api<HubPriceAnswer>("POST", "public/my/price", { companyId: b.companyId, answers }, { "x-portal-token": token }),
    placeholderData: (prev) => prev,
  });
  const p = price.data?.price ?? null;
  const pickService = (id: string) => {
    setHourly(false);
    setHome((h) => ({
      ...h,
      serviceId: id,
      often: repeatsOf(id) ? (h.often === "once" ? startOften(id) : h.often) : "once",
      extras: {},
    }));
  };
  const book = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ link: string }>(
        "POST",
        "public/my/quote",
        { companyId: b.companyId, answers },
        { "x-portal-token": token },
      );
      window.location.assign(r.link);
    } catch (e) {
      setErr(errText(e));
      setBusy(false);
    }
  };
  const extras = s.extras.filter((x) => x.only === null || x.only === home.serviceId);
  const homeOften = repeatsOf(home.serviceId) ? [...s.repeats, { id: "once", label: "One time" }] : [];
  // Every 2 weeks first, then every week, once a month, and one time last.
  homeOften.sort((x, y) => OFTEN_ORDER.indexOf(x.id) - OFTEN_ORDER.indexOf(y.id));
  return (
    <div className="my-stack">
      <div className="field">
        <span className="label">What kind of clean?</span>
        <div className="my-chips">
          {s.services.map((x) => (
            <button key={x.id} type="button" aria-pressed={!hourly && home.serviceId === x.id} onClick={() => pickService(x.id)}>
              {x.label}
            </button>
          ))}
          <button type="button" aria-pressed={hourly} onClick={() => setHourly(true)}>
            Maid service by the hour
          </button>
          <button type="button" onClick={onOther}>
            Something else
          </button>
        </div>
      </div>
      {hourly ? (
        <>
          <p className="muted small">{moneyExact(s.hourlyCents)} an hour for each cleaner.</p>
          <div className="aq-steppers">
            <Count
              label="Cleaners"
              value={byHour.cleaners}
              min={1}
              max={6}
              set={(n) => setByHour((h) => ({ ...h, cleaners: n }))}
            />
            <Count
              label="Hours"
              value={byHour.hours}
              min={1}
              max={10}
              step={0.5}
              set={(n) => setByHour((h) => ({ ...h, hours: n }))}
            />
          </div>
          <Often
            options={HOURLY_ORDER.map((id) => ({ id, label: HOURLY_OFTEN_WORDS[id] }))}
            value={byHour.often}
            set={(o) => setByHour((h) => ({ ...h, often: o as HourlyAnswers["often"] }))}
          />
        </>
      ) : (
        <>
          {home.sizeBy === "sqft" ? (
            <label className="field">
              <span className="label">Square feet</span>
              <div className="input">
                <input
                  inputMode="numeric"
                  value={String(home.sqft || "")}
                  onChange={(e) =>
                    setHome((h) => ({ ...h, sqft: Math.min(50_000, Number(e.target.value.replace(/\D/g, "")) || 0) }))
                  }
                />
              </div>
            </label>
          ) : (
            <div className="field">
              <span className="label">How big is your home?</span>
              <p className="muted small">Kitchen and living room are already included.</p>
              <div className="aq-steppers">
                <Count label="Bedrooms" value={home.beds} min={0} max={20} set={(n) => setHome((h) => ({ ...h, beds: n }))} />
                <Count
                  label="Bathrooms"
                  value={home.baths}
                  min={1}
                  max={20}
                  step={0.5}
                  set={(n) => setHome((h) => ({ ...h, baths: n }))}
                />
                <Count
                  label="Other rooms"
                  value={home.other}
                  min={0}
                  max={20}
                  set={(n) => setHome((h) => ({ ...h, other: n }))}
                />
              </div>
            </div>
          )}
          {homeOften.length ? (
            <Often
              options={homeOften}
              value={home.often}
              set={(o) => setHome((h) => ({ ...h, often: o as HomeAnswers["often"] }))}
            />
          ) : null}
          {extras.length ? (
            <div className="field">
              <span className="label">Add-ons for the first visit (you can skip this)</span>
              <div className="my-chips">
                {extras.map((x) => {
                  const on = Boolean(home.extras[x.id]);
                  return (
                    <button
                      key={x.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setHome((h) => ({ ...h, extras: { ...h.extras, [x.id]: on ? false : x.each ? 1 : true } }))}
                    >
                      {x.label} +{moneyExact(home.often !== "once" && x.planCents !== null ? x.planCents : x.cents)}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </>
      )}
      {/* The price and Book it stay at the bottom of the sheet while the answers above scroll. */}
      <div className="my-quote-foot">
        <div className="my-price" aria-live="polite">
          {p ? (
            p.repeat ? (
              p.planStart || p.firstVisitCents === p.repeat.perVisitCents ? (
                <>
                  <b>{moneyExact(p.repeat.perVisitCents)}</b>
                  <span>{p.repeat.label.toLowerCase()}, starting with your first visit</span>
                  {p.planStart ? (
                    <span className="muted small">Your home was cleaned lately, so there is no first visit price.</span>
                  ) : null}
                </>
              ) : (
                <>
                  <b>{moneyExact(p.firstVisitCents)}</b>
                  <span>
                    first visit, then {moneyExact(p.repeat.perVisitCents)} {p.repeat.label.toLowerCase()}
                  </span>
                </>
              )
            ) : (
              <>
                <b>{moneyExact(p.firstVisitCents)}</b>
                <span>one time</span>
              </>
            )
          ) : price.isFetching ? (
            <span className="muted">Working out your price</span>
          ) : (
            <span className="muted">We can't price that here. Tap Something else and we will.</span>
          )}
        </div>
        {err ? <Banner type="error">{err}</Banner> : null}
        <Button size="large" fullWidth loading={busy} disabled={!p || price.isFetching} onClick={() => void book()}>
          Book it
        </Button>
        <p className="muted small">Next you pick a day. Nothing to pay today.</p>
      </div>
    </div>
  );
}

/** Every 2 weeks first, one time last. */
const OFTEN_ORDER = ["biweekly", "weekly", "monthly", "once"];
const HOURLY_ORDER: HourlyAnswers["often"][] = ["biweekly", "weekly", "daily", "monthly", "once"];

function Often({ options, value, set }: { options: { id: string; label: string }[]; value: string; set: (id: string) => void }) {
  return (
    <div className="field">
      <span className="label">How often?</span>
      <div className="my-chips" role="radiogroup">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={o.id === value}
            aria-pressed={o.id === value}
            onClick={() => set(o.id)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A number with a minus and a plus (rooms, cleaners, hours), the same as on the quote page. */
function Count({
  label,
  value,
  min,
  max,
  step = 1,
  set,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  set: (n: number) => void;
}) {
  return (
    <div className="aq-stepper">
      <span className="aq-label">{label}</span>
      <div className="aq-stepper-row">
        <button
          type="button"
          aria-label={`Fewer ${label.toLowerCase()}`}
          disabled={value <= min}
          onClick={() => set(Math.max(min, value - step))}
        >
          −
        </button>
        <b aria-live="polite">{value}</b>
        <button
          type="button"
          aria-label={`More ${label.toLowerCase()}`}
          disabled={value >= max}
          onClick={() => set(Math.min(max, value + step))}
        >
          +
        </button>
      </div>
    </div>
  );
}

/**
 * Wallet: the card on file and a way to replace it with another working card. There is no Remove: a card stays on file at
 * all times (owner, 2026-10-30). A client who never gave one can add one.
 */
function MyWallet({ b, token }: PageProps) {
  const [adding, setAdding] = useState(false);
  return (
    <>
      <h1>Wallet</h1>
      {b.card ? (
        <div className="my-card">
          <span className="my-card-row">
            <span className="my-card-title">
              {b.card.brand} ending {b.card.last4}
            </span>
            <Icon name="money" size={22} />
          </span>
          <span className="muted">
            {b.card.auto ? "Your visits are paid with this card when they are done." : "Kept on file for your visits."}
          </span>
          {b.cardsOn ? (
            <Button type="secondary" fullWidth onClick={() => setAdding(true)}>
              Use a different card
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="my-card">
          <span className="my-card-title">No card on file</span>
          <span className="muted">
            {b.cardsOn ? "Save a card so paying takes one tap." : "Save a card when you pay your next invoice."}
          </span>
          {b.cardsOn ? (
            <Button size="large" fullWidth onClick={() => setAdding(true)}>
              Add a card
            </Button>
          ) : null}
        </div>
      )}
      {b.card ? (
        <p className="muted small">We keep one card on file for your visits. A new card replaces this one once it checks out.</p>
      ) : null}
      <CardSheet b={b} token={token} open={adding} onClose={() => setAdding(false)} />
    </>
  );
}

interface CardStart {
  intentId: string;
  clientSecret: string;
  publishableKey: string | null;
  fake: boolean;
}

/** Save a card with Stripe's card fields on our own page (owner rule: no Stripe pages). */
function CardSheet({ b, token, open, onClose }: { b: PortalBrand; token: string; open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const box = useRef<HTMLDivElement>(null);
  const [setup, setSetup] = useState<CardStart | null>(null);
  const [stripe, setStripe] = useState<{ s: Stripe; e: StripeElements } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const head = { "x-portal-token": token };
  const readOnly = useLocked();
  useEffect(() => {
    if (!open || readOnly) return;
    const live = { on: true };
    // Read through a function: the sheet may close while Stripe loads.
    const gone = () => !live.on;
    setErr(null);
    void (async () => {
      try {
        const made = await api<CardStart>(
          "POST",
          "public/my/card-start",
          { companyId: b.companyId },
          { "x-portal-token": token },
        );
        if (gone()) return;
        setSetup(made);
        if (!made.fake && made.publishableKey) {
          const st = await loadStripe(made.publishableKey);
          if (!st || gone()) return;
          const el = st.elements({ clientSecret: made.clientSecret });
          setStripe({ s: st, e: el });
          requestAnimationFrame(() => {
            if (box.current) el.create("payment").mount(box.current);
          });
        }
      } catch (e) {
        if (!gone()) setErr(errText(e));
      }
    })();
    return () => {
      live.on = false;
      setSetup(null);
      setStripe(null);
    };
  }, [open, b.companyId, token, readOnly]);
  const save = async () => {
    if (!setup) return;
    setBusy(true);
    setErr(null);
    try {
      if (stripe) {
        const r = await stripe.s.confirmSetup({
          elements: stripe.e,
          confirmParams: { return_url: window.location.href },
          redirect: "if_required",
        });
        if (r.error) throw new Error(r.error.message ?? "The card was not saved.");
      }
      const r = await api<{ saved: boolean }>(
        "POST",
        "public/my/card-done",
        { companyId: b.companyId, intentId: setup.intentId },
        head,
      );
      if (!r.saved) throw new Error("The card was not saved. Try again.");
      await qc.invalidateQueries({ queryKey: ["my"] });
      onClose();
    } catch (e) {
      setErr(e instanceof Error && !(e instanceof TypeError) ? e.message : errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Sheet open={open} title={b.card ? "Use a different card" : "Add a card"} onClose={onClose} changes>
      <div className="my-stack">
        <p className="muted small">{b.name} keeps this card on file so paying is quick. Nothing is charged now.</p>
        {setup && !setup.fake && setup.publishableKey ? <div ref={box} className="public-card" /> : null}
        {setup && (setup.fake || !setup.publishableKey) ? (
          <Banner type="notice">Test mode here. Save uses the test card 4242.</Banner>
        ) : null}
        {!setup && !err ? <Skeleton rows={2} /> : null}
        {err ? <Banner type="error">{err}</Banner> : null}
        <Button size="large" fullWidth loading={busy} disabled={!setup} onClick={() => void save()}>
          Save card
        </Button>
      </div>
    </Sheet>
  );
}

function MyRefer({ b }: PageProps) {
  const [copied, setCopied] = useState(false);
  const r = b.refer;
  if (!r)
    return (
      <>
        <h1>Refer a friend</h1>
        <p className="muted">Tell a friend about us. Ask them to say your name when they call.</p>
      </>
    );
  const amt = money(r.amountCents);
  const visit = tradeOf(b.trade).visit;
  const words = `Your friend gets ${amt} off their first ${visit}. You get ${amt} off your next one after their first ${visit}.`;
  const share = async () => {
    const text = `I use ${b.name} and love them. Get ${amt} off your first ${visit} with my link.`;
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({ title: b.name, text, url: r.link });
        return;
      } catch {
        // They closed the share sheet; nothing to do.
        return;
      }
    }
    await navigator.clipboard.writeText(`${text} ${r.link}`).catch(() => null);
    setCopied(true);
  };
  return (
    <>
      <h1>
        Give {amt}, get {amt}
      </h1>
      <p>{words}</p>
      <div className="my-linkbox">
        <span>{r.link.replace(/^https?:\/\//, "")}</span>
      </div>
      <Button size="large" fullWidth onClick={() => void share()}>
        Share my link
      </Button>
      <Button
        type="tertiary"
        fullWidth
        onClick={() =>
          void navigator.clipboard
            .writeText(r.link)
            .then(() => setCopied(true))
            .catch(() => null)
        }
      >
        {copied ? "Copied" : "Copy link"}
      </Button>
    </>
  );
}

function MyContact({ b, onOut }: PageProps & { onOut: () => void }) {
  return (
    <>
      <h1>Contact {b.name}</h1>
      {b.phone ? (
        <>
          <a className="btn primary large full" href={`sms:${b.phone}`}>
            Text us
          </a>
          <a className="btn secondary large full" href={`tel:${b.phone}`}>
            Call {phoneText(b.phone)}
          </a>
        </>
      ) : (
        <p className="muted">Reply to any text from us and we will see it.</p>
      )}
      {b.email ? (
        <a className="btn tertiary full" href={`mailto:${b.email}`}>
          Email {b.email}
        </a>
      ) : null}
      <hr className="my-hr" />
      <Button type="tertiary" fullWidth icon="signout" onClick={onOut}>
        Log out
      </Button>
    </>
  );
}

function MyMore({
  b,
  go,
  onOut,
  manyBrands,
  onSwitch,
}: PageProps & { onOut: () => void; manyBrands: boolean; onSwitch: () => void }) {
  const open = b.quotes.filter((q) => q.status === "sent" && !q.offer).length;
  const offered = b.quotes.filter((q) => q.offer).length;
  const rows: { icon: IconName; label: string; sub?: string | undefined; on: () => void }[] = [
    {
      icon: "quotes",
      label: offered ? "Offers and quotes" : "Quotes",
      sub: offered
        ? `${String(offered)} ${offered === 1 ? "offer" : "offers"} for you`
        : open
          ? `${String(open)} waiting for your OK`
          : undefined,
      on: () => go("quotes"),
    },
    { icon: "requests", label: "Book or ask", on: () => go("requests") },
    {
      icon: "money",
      label: "Wallet",
      sub: b.card ? `${b.card.brand} ending ${b.card.last4}` : "No card on file",
      on: () => go("wallet"),
    },
    ...(b.refer
      ? [
          {
            icon: "tag" as IconName,
            label: "Refer a friend",
            sub: `Give ${money(b.refer.amountCents)}, get ${money(b.refer.amountCents)}`,
            on: () => go("refer"),
          },
        ]
      : []),
    { icon: "phone", label: "Contact us", on: () => go("contact") },
    ...(manyBrands ? [{ icon: "building" as IconName, label: "Switch business", sub: b.name, on: onSwitch }] : []),
  ];
  return (
    <>
      <h1>More</h1>
      <ul className="my-rows">
        {rows.map((r) => (
          <li key={r.label}>
            <button type="button" className="my-row" onClick={r.on}>
              <span className="my-row-icon">
                <Icon name={r.icon} size={20} />
              </span>
              <span className="my-row-text">
                <b>{r.label}</b>
                {r.sub ? <span>{r.sub}</span> : null}
              </span>
              <Icon name="chevronRight" size={20} />
            </button>
          </li>
        ))}
        <li>
          <button type="button" className="my-row" onClick={onOut}>
            <span className="my-row-icon">
              <Icon name="signout" size={20} />
            </span>
            <span className="my-row-text">
              <b>Log out</b>
            </span>
          </button>
        </li>
      </ul>
    </>
  );
}
